/**
 * Footer.tsx
 * --------------------------------------------------------------------------
 * Footer global — versión "tarjeta nocturna" alineada con la referencia
 * visual del cliente (mockup 2026-05-22):
 *
 *  - Desktop: una tarjeta horizontal de 5 columnas con fondo violeta noche
 *    en gradiente, borde fino dorado envejecido, esquinas redondeadas grandes,
 *    sombra suave, ornamentos dorados sutiles (luna creciente arriba-derecha,
 *    rama abajo-derecha, estrellas dispersas con baja opacidad).
 *  - Columnas: Marca · Navegación · Recursos · Contacto · Boletín.
 *  - Separadores verticales sutiles entre columnas.
 *  - Boletín con input cremoso + botón violeta-dorado con ícono Sparkles.
 *
 *  - Tablet (md): 2-3 columnas, mismo chrome oscuro.
 *  - Mobile  (base): 1 columna stack, mismo chrome oscuro, sin overflow.
 *
 * SOLO TOKENS del sistema (cero hex literal). El bloque `contact` y
 * `social_links` se siguen leyendo de `site_settings` vía `useSiteSettings`
 * (el admin sigue editándolos en /admin/configuracion → Contacto / Social);
 * los textos del usuario en el spec se usan como FALLBACK si la BD viene
 * vacía. Ningún cambio en backend, rutas, lógica de auth ni newsletter.
 *
 * El handler del boletín es VISUAL (toast informativo) — clonado del patrón
 * que ya usa Home.tsx mientras se conecta la Edge Function de suscripción.
 *
 * ARIA: `role="contentinfo"` + `<nav aria-label>` por columna + `aria-label`
 * en los íconos sociales (cubierto por SocialBadge). El form del boletín usa
 * un FormField con `hideLabel` accesible para SR.
 */
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  Clock,
  Facebook,
  Instagram,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Sparkles,
  Youtube,
} from 'lucide-react';

import { Routes } from '../../lib/routes';
import { cn } from '../../lib/cn';
import { Button } from '../ui/Button';
import { FormField } from '../ui/FormField';
import { Input } from '../ui/Input';
import { useToast } from '../ui/ToastProvider';
import { useSiteSettings, type SiteSocialLinks } from '../../features/home/api';

// ---------------------------------------------------------------------------
// Tipos y helpers locales
// ---------------------------------------------------------------------------

interface SiteContact {
  email?: string;
  phone?: string;
  whatsapp?: string;
  address?: string;
  hours?: string;
}

/**
 * QA 2026-09-23: acá vivía un `CONTACT_FALLBACK` con correo, teléfono,
 * dirección y horario quemados —`hola@ceciluzarte.com`, `+57 300 123 4567`—
 * que se usaban cuando `site_settings.contact` venía vacío.
 *
 * El problema no era tenerlos sino que eran **datos inventados mostrados como
 * reales**: un visitante podía escribir a un correo que no existe o llamar a
 * un teléfono que no es de nadie, y el sitio se veía perfectamente normal. Un
 * dato de contacto falso es peor que ninguno.
 *
 * Ahora el footer sólo muestra lo que el admin cargó en `site_settings`. Lo
 * que falte, no se pinta. Si el bloque de contacto se ve vacío, es señal de
 * que hay que llenarlo en `/admin/configuracion → Contacto`.
 */

function readContact(raw: unknown): SiteContact {
  if (!raw || typeof raw !== 'object') return {};
  const c = raw as Record<string, unknown>;
  return {
    email: typeof c.email === 'string' ? c.email : undefined,
    phone: typeof c.phone === 'string' ? c.phone : undefined,
    whatsapp: typeof c.whatsapp === 'string' ? c.whatsapp : undefined,
    address: typeof c.address === 'string' ? c.address : undefined,
    hours: typeof c.hours === 'string' ? c.hours : undefined,
  };
}

function readSocialLinks(raw: unknown): SiteSocialLinks {
  if (!raw || typeof raw !== 'object') return {};
  const out: SiteSocialLinks = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === 'string' && value.length > 0) {
      out[key] = value;
    }
  }
  return out;
}

/** Lucide no incluye Pinterest. Inline con `currentColor`, sin hex. */
function PinterestGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable={false}
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M11 8.5h2.5a2.5 2.5 0 0 1 0 5H11" />
      <path d="M11 8.5v9.5" />
    </svg>
  );
}

function buildWhatsappHref(raw: string): string {
  if (/^https?:\/\//.test(raw)) return raw;
  const digits = raw.replace(/\D/g, '');
  return `https://wa.me/${digits}`;
}

// ---------------------------------------------------------------------------
// Sets de links (locales al footer, no afectan al Navbar)
// ---------------------------------------------------------------------------

interface FooterLink {
  label: string;
  href: string;
  /** Si true, se renderiza con <a> externo en vez de <Link>. */
  external?: boolean;
}

/**
 * Lista de navegación local al footer — usa la nomenclatura del spec del
 * cliente (incluye "Galería" + "Sobre mí", literales de la referencia visual)
 * SIN modificar el `NAV_ITEMS` del Navbar.
 */
const FOOTER_NAV_LINKS: readonly FooterLink[] = [
  { label: 'Inicio', href: Routes.home },
  { label: 'Terapias', href: Routes.therapies },
  { label: 'Tienda', href: Routes.shop },
  { label: 'Galería', href: Routes.gallery },
  { label: 'Sobre mí', href: Routes.about },
  { label: 'Eventos', href: Routes.events },
  { label: 'Contacto', href: Routes.contact },
];

/**
 * Recursos editoriales. Apuntan a rutas reales del sitio donde corresponde:
 * "Recursos" agrupa Blog/Guías (placeholder /recursos), FAQ y Testimonios
 * viven dentro de páginas existentes con anchors.
 */
const FOOTER_RESOURCE_LINKS: readonly FooterLink[] = [
  { label: 'Blog', href: Routes.resources },
  { label: 'Guías gratuitas', href: Routes.resources },
  { label: 'Preguntas frecuentes', href: `${Routes.contact}#faq` },
  { label: 'Testimonios', href: `${Routes.home}#testimonios` },
  { label: 'Políticas', href: Routes.privacy },
  { label: 'Términos y condiciones', href: Routes.terms },
];

// ---------------------------------------------------------------------------
// Sub-componentes
// ---------------------------------------------------------------------------

interface SocialDef {
  key: 'instagram' | 'facebook' | 'pinterest' | 'youtube' | 'whatsapp' | 'mail';
  label: string;
  href: string;
}

/**
 * Cecilia mantiene dos perfiles profesionales por red — `ceciluz_arte` y
 * `ceciland98` (Sanar Hacia el Bienestar). Cuando ambos aplican para una
 * plataforma (Instagram/Facebook), el badge deja de ser un `<a>` y pasa a
 * abrir un pequeño popover con las dos opciones. URLs hardcoded acá porque
 * son constantes de negocio (dos cuentas, marcas separadas) — no vale la
 * pena versionarlas en `site_settings`.
 */
interface SocialProfileOption {
  label: string;
  href: string;
}

const INSTAGRAM_PROFILES: readonly SocialProfileOption[] = [
  { label: 'Ceciluz Arte', href: 'https://www.instagram.com/ceciluz_arte' },
  {
    label: 'Sanar Hacia el Bienestar',
    href: 'https://www.instagram.com/ceciland98?igsh=MWVvb2RiazZ6dm1nYQ==',
  },
];

const FACEBOOK_PROFILES: readonly SocialProfileOption[] = [
  {
    label: 'Ceciluz Arte',
    href: 'https://www.facebook.com/share/1BD7Hc6kV1/?mibextid=wwXIfr',
  },
  {
    label: 'Sanar Hacia el Bienestar',
    href: 'https://www.facebook.com/share/193TQkoTuZ/?mibextid=wwXIfr',
  },
];

function SocialBadge({ social }: { social: SocialDef }) {
  const Glyph = (() => {
    switch (social.key) {
      case 'instagram':
        return <Instagram className="h-5 w-5" aria-hidden="true" strokeWidth={1.5} />;
      case 'facebook':
        return <Facebook className="h-5 w-5" aria-hidden="true" strokeWidth={1.5} />;
      case 'youtube':
        return <Youtube className="h-5 w-5" aria-hidden="true" strokeWidth={1.5} />;
      case 'pinterest':
        return <PinterestGlyph className="h-5 w-5" />;
      case 'whatsapp':
        return <MessageCircle className="h-5 w-5" aria-hidden="true" strokeWidth={1.5} />;
      case 'mail':
        return <Mail className="h-5 w-5" aria-hidden="true" strokeWidth={1.5} />;
    }
  })();

  return (
    <a
      href={social.href}
      target={social.key === 'mail' ? undefined : '_blank'}
      rel={social.key === 'mail' ? undefined : 'noopener noreferrer'}
      aria-label={social.label}
      className={cn(
        // Touch target >= 44x44 (WCAG 2.5.5).
        'inline-flex h-11 w-11 items-center justify-center rounded-pill',
        // Círculo violeta oscuro con borde dorado e icono dorado.
        'bg-primary-800/40 border border-gold-500/55 text-gold-500',
        'hover:bg-gold-500 hover:text-primary-900 hover:border-gold-500',
        'transition-colors duration-2 ease-out',
      )}
    >
      {Glyph}
    </a>
  );
}

/**
 * SocialDropdown — variante del `SocialBadge` para redes con MÚLTIPLES
 * perfiles (Instagram / Facebook). El badge queda idéntico visualmente al
 * `<a>` estándar (mismo círculo violeta con borde dorado), pero al hacer
 * click abre un popover con la lista de opciones. Cada opción es un `<a>`
 * con `target="_blank" rel="noopener noreferrer"`.
 *
 * A11y (patrón WAI-ARIA "menu button"):
 *  · Trigger: `<button aria-haspopup="menu" aria-expanded aria-controls>`.
 *  · Panel:   `role="menu"` con `<a role="menuitem">` por opción.
 *  · Escape cierra y devuelve el foco al trigger.
 *  · Click fuera cierra sin robar foco.
 *  · Sin librería externa — el DS del proyecto no incluye un Dropdown
 *    reutilizable y el ticket restringe instalar dependencias nuevas.
 */
interface SocialDropdownProps {
  socialKey: 'instagram' | 'facebook';
  label: string;
  options: readonly SocialProfileOption[];
}

function SocialDropdown({ socialKey, label, options }: SocialDropdownProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuId = useId();

  const Glyph =
    socialKey === 'instagram' ? (
      <Instagram className="h-5 w-5" aria-hidden="true" strokeWidth={1.5} />
    ) : (
      <Facebook className="h-5 w-5" aria-hidden="true" strokeWidth={1.5} />
    );

  // Cerrar al click fuera + Escape (devuelve foco al trigger).
  useEffect(() => {
    if (!open) return;
    const onPointer = (ev: PointerEvent) => {
      const target = ev.target as Node | null;
      if (!target || !containerRef.current) return;
      if (!containerRef.current.contains(target)) setOpen(false);
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`${label} — elegir perfil`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        className={cn(
          // Mismo styling que <SocialBadge>: círculo violeta oscuro con
          // borde dorado e icono dorado. El estilo visual del badge NO
          // cambia — el usuario reconoce el mismo affordance.
          'inline-flex h-11 w-11 items-center justify-center rounded-pill',
          'bg-primary-800/40 border border-gold-500/55 text-gold-500',
          'hover:bg-gold-500 hover:text-primary-900 hover:border-gold-500',
          'transition-colors duration-2 ease-out',
          // Cuando el menú está abierto pintamos el badge con el fondo
          // dorado sólido — feedback claro de estado sin cambiar el shape.
          open && 'bg-gold-500 text-primary-900 border-gold-500',
        )}
      >
        {Glyph}
      </button>

      {/* Popover — anclado ARRIBA del badge (`bottom-full`) porque el
          Footer está pegado al bottom del viewport. Fondo cream claro con
          borde dorado sutil + shadow-elev2 + rounded-md — coherente con
          Card/Modal/Toast del DS. Sale con transición sutil de opacidad
          y traslación de 4 px. */}
      <div
        id={menuId}
        role="menu"
        aria-label={`Perfiles de ${label}`}
        className={cn(
          'absolute left-1/2 z-50 -translate-x-1/2 bottom-full mb-3 min-w-[220px]',
          'rounded-md border border-gold-500/40 bg-cream-50 shadow-elev2',
          'p-1.5 origin-bottom transition duration-2 ease-out',
          open
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 translate-y-1 pointer-events-none',
        )}
      >
        <p
          className="px-3 pt-1.5 pb-1 text-caption uppercase tracking-[0.12em] text-primary-700/70"
          aria-hidden="true"
        >
          {label}
        </p>
        <ul className="flex flex-col">
          {options.map((opt) => (
            <li key={opt.label} className="contents">
              <a
                role="menuitem"
                href={opt.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className={cn(
                  'block rounded px-3 py-2 text-body text-primary-900',
                  'hover:bg-cream-100 hover:text-primary-700',
                  'focus-visible:outline-none focus-visible:bg-cream-100 focus-visible:text-primary-700',
                  'transition-colors duration-2 ease-out',
                )}
              >
                {opt.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

interface FooterHeadingProps {
  id?: string;
  children: React.ReactNode;
}

function FooterHeading({ id, children }: FooterHeadingProps) {
  return (
    <h2
      id={id}
      className={cn('font-display text-h4 text-gold-soft', 'uppercase tracking-[0.16em]', 'mb-5')}
    >
      {children}
    </h2>
  );
}

/** Link de columna — estilo crema con hover dorado. */
function FooterLinkItem({ href, label, external }: FooterLink) {
  const cls = cn(
    'inline-block text-body text-cream-100/80',
    'hover:text-gold-soft transition-colors duration-2 ease-out',
  );
  if (external) {
    return (
      <a href={href} className={cls} rel="noopener noreferrer">
        {label}
      </a>
    );
  }
  return (
    <Link to={href} className={cls}>
      {label}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Ornamentos decorativos
// ---------------------------------------------------------------------------

/** Luna creciente arriba-derecha. SVG inline, sin hex, hereda currentColor. */
function MoonOrnament({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 64 64"
      className={cn('text-gold-500', className)}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.25}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable={false}
    >
      <path d="M44 12 a22 22 0 1 0 8 28 18 18 0 0 1 -8 -28 z" />
    </svg>
  );
}

/** Rama dorada abajo-derecha. Inline SVG, sólo trazos con `currentColor`. */
function BranchOrnament({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 160 80"
      className={cn('text-gold-500', className)}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.25}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable={false}
    >
      {/* Tallo principal en curva suave */}
      <path d="M0 70 C 40 60, 80 40, 160 10" />
      {/* Hojas a ambos lados */}
      <path d="M30 58 q 8 -12 22 -10" />
      <path d="M52 48 q -6 12 -18 14" />
      <path d="M70 42 q 10 -14 26 -10" />
      <path d="M96 32 q -6 12 -20 14" />
      <path d="M110 24 q 10 -10 24 -8" />
      <path d="M134 16 q -6 10 -18 12" />
    </svg>
  );
}

/** Estrella decorativa pequeña (✦) dorada — usada en varios puntos. */
function StarDot({ className }: { className?: string }) {
  return (
    <span aria-hidden="true" className={cn('select-none text-gold-500', className)}>
      ✦
    </span>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export function Footer() {
  const siteSettings = useSiteSettings();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Única fuente: `site_settings`. Los campos ausentes quedan `undefined` y
  // cada bloque decide no renderizarse (ver más abajo).
  const contact: SiteContact = readContact(siteSettings.data?.contact);
  const social = readSocialLinks(siteSettings.data?.social_links);

  const socialList: SocialDef[] = [];
  // Instagram y Facebook son dropdowns fijos (Cecilia mantiene dos
  // perfiles por red — ver `INSTAGRAM_PROFILES` / `FACEBOOK_PROFILES`
  // arriba). Se rendrean SIEMPRE, sin depender de `site_settings` porque
  // las opciones son constantes de negocio. El campo `href` del `SocialDef`
  // queda vacío — el dropdown ignora `href` cuando `key` es 'instagram'
  // o 'facebook' y usa la lista de opciones hardcoded.
  socialList.push({ key: 'instagram', label: 'Instagram', href: '' });
  socialList.push({ key: 'facebook', label: 'Facebook', href: '' });
  if (social.pinterest)
    socialList.push({ key: 'pinterest', label: 'Pinterest', href: social.pinterest });
  if (social.youtube) socialList.push({ key: 'youtube', label: 'YouTube', href: social.youtube });
  if (contact.whatsapp)
    socialList.push({
      key: 'whatsapp',
      label: 'WhatsApp',
      href: buildWhatsappHref(contact.whatsapp),
    });
  if (contact.email)
    socialList.push({
      key: 'mail',
      label: `Enviar correo a ${contact.email}`,
      href: `mailto:${contact.email}`,
    });

  // Handler visual del boletín (sin backend). Conserva el patrón placeholder
  // de Home.tsx mientras se conecta la Edge Function `newsletter-subscribe`.
  const handleNewsletter = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    // Sin lógica de negocio nueva — sólo toast informativo + reset.
    toast({
      variant: 'info',
      title: '¡Gracias por inscribirte!',
      description: 'Pronto activaremos las suscripciones al boletín.',
    });
    setEmail('');
    setSubmitting(false);
  };

  const year = new Date().getFullYear();

  return (
    <footer role="contentinfo" className="bg-cream-100">
      {/* Wrapper: deja respiración a ambos lados del card oscuro. */}
      <div className="mx-auto max-w-[1440px] px-4 md:px-6 py-10 lg:py-12">
        {/* ============ Tarjeta oscura ============ */}
        {/*
          NOTA: el `overflow-hidden` que teníamos aquí antes se movió a la
          capa de ornamentos de abajo. La capa decorativa (halos, luna,
          ramas) SÍ necesita recortarse a las esquinas redondeadas del
          card, pero el card en sí debe permitir que sus hijos salgan
          para no clipear el popover de los `SocialDropdown` (Instagram /
          Facebook) que se despliega hacia arriba fuera del footer.
        */}
        <div
          className={cn(
            'relative isolate',
            'rounded-xl lg:rounded-[28px]',
            'border border-gold-500/45',
            'shadow-elev2',
            // Gradiente violeta noche profundo → violeta marca → violeta marca.
            'bg-gradient-to-br from-primary-900 via-primary-800 to-primary-vivid',
            'text-cream-100',
          )}
        >
          {/* ---- Capa de ornamentos decorativos (no interactiva) ----
              El `overflow-hidden` + `rounded-xl lg:rounded-[28px]`
              vive ACÁ (no en el card padre) para que los halos y ramas
              posicionados con `-top-*` / `-left-*` no se salgan del
              contorno curvo, sin bloquear a otros hijos del card
              (como el popover del dropdown). */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 select-none overflow-hidden rounded-xl lg:rounded-[28px]"
          >
            {/* Luna creciente arriba a la derecha */}
            <MoonOrnament className="absolute right-8 top-6 h-12 w-12 opacity-80 lg:h-16 lg:w-16" />

            {/* Halo dorado superior derecho */}
            <div className="absolute -right-24 -top-24 h-64 w-64 rounded-pill bg-gold-500/10 blur-3xl" />
            {/* Halo violeta inferior izquierdo */}
            <div className="absolute -left-32 bottom-0 h-72 w-72 rounded-pill bg-primary-500/15 blur-3xl" />

            {/* Estrellas distribuidas — posiciones fijas, opacidades variadas. */}
            <StarDot className="absolute left-[12%] top-[18%] text-xs opacity-80" />
            <StarDot className="absolute left-[28%] top-[64%] text-[10px] opacity-60" />
            <StarDot className="absolute left-[42%] top-[12%] text-sm opacity-50" />
            <StarDot className="absolute left-[58%] top-[78%] text-xs opacity-70" />
            <StarDot className="absolute left-[68%] top-[28%] text-[10px] opacity-55" />
            <StarDot className="absolute left-[82%] top-[58%] text-xs opacity-65" />
            <StarDot className="absolute left-[18%] top-[86%] text-[10px] opacity-50" />
            <StarDot className="absolute left-[91%] top-[14%] text-[10px] opacity-60" />

            {/* Rama dorada inferior derecha */}
            <BranchOrnament className="absolute bottom-3 right-6 hidden h-20 w-44 opacity-70 md:block lg:bottom-6 lg:right-10 lg:h-24 lg:w-56" />
          </div>

          {/* ---- Contenido del footer ---- */}
          <div className="relative grid grid-cols-1 gap-10 p-8 md:grid-cols-2 md:gap-12 md:p-10 lg:grid-cols-[1.4fr_1fr_1.1fr_1.3fr_1.5fr] lg:gap-10 lg:p-12">
            {/* ============ Columna 1 — Marca ============ */}
            <div className="md:col-span-2 lg:col-span-1">
              <Link
                to={Routes.home}
                aria-label="Ceciluz Arte, ir al inicio"
                className="group inline-flex items-center"
              >
                {/* Reusa el logo principal — el filtro invert lo vuelve crema/blanco
                    para que se vea bien sobre el violeta noche. */}
                <img
                  src="/brand/logo-ceciluz-arte.webp"
                  alt="Ceciluz Arte"
                  className={cn(
                    'h-16 w-auto brightness-0 invert',
                    'transition-opacity duration-2 ease-out',
                    'group-hover:opacity-90',
                  )}
                  decoding="async"
                  draggable={false}
                />
              </Link>

              {/* Tagline editorial bajo el logo */}
              <p className="mt-6 text-body text-cream-100/80 leading-relaxed max-w-xs">
                Arte que sana. Energía que transforma. Alma que conecta.
              </p>

              {/* Redes sociales */}
              {socialList.length > 0 && (
                <ul
                  className="mt-6 flex flex-wrap items-center gap-2.5"
                  aria-label="Redes sociales"
                >
                  {socialList.map((s) => (
                    <li key={s.key}>
                      {s.key === 'instagram' ? (
                        <SocialDropdown
                          socialKey="instagram"
                          label="Instagram"
                          options={INSTAGRAM_PROFILES}
                        />
                      ) : s.key === 'facebook' ? (
                        <SocialDropdown
                          socialKey="facebook"
                          label="Facebook"
                          options={FACEBOOK_PROFILES}
                        />
                      ) : (
                        <SocialBadge social={s} />
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* ============ Columna 2 — Navegación ============ */}
            <nav
              aria-labelledby="footer-nav-heading"
              className={cn(
                // Separador vertical sutil a la izquierda en desktop.
                'lg:border-l lg:border-gold-soft/20 lg:pl-8',
              )}
            >
              <FooterHeading id="footer-nav-heading">Navegación</FooterHeading>
              <ul className="space-y-3">
                {FOOTER_NAV_LINKS.map((link) => (
                  <li key={link.href + link.label}>
                    <FooterLinkItem {...link} />
                  </li>
                ))}
              </ul>
            </nav>

            {/* ============ Columna 3 — Recursos ============ */}
            <nav
              aria-labelledby="footer-resources-heading"
              className="lg:border-l lg:border-gold-soft/20 lg:pl-8"
            >
              <FooterHeading id="footer-resources-heading">Recursos</FooterHeading>
              <ul className="space-y-3">
                {FOOTER_RESOURCE_LINKS.map((link) => (
                  <li key={link.href + link.label}>
                    <FooterLinkItem {...link} />
                  </li>
                ))}
              </ul>
            </nav>

            {/* ============ Columna 4 — Contacto ============ */}
            <div className="lg:border-l lg:border-gold-soft/20 lg:pl-8">
              <FooterHeading>Contacto</FooterHeading>
              <ul className="space-y-3 text-body text-cream-100/85">
                {contact.email && (
                  <li className="flex items-start gap-3">
                    <Mail
                      className="h-5 w-5 shrink-0 mt-0.5 text-gold-500"
                      aria-hidden="true"
                      strokeWidth={1.5}
                    />
                    <a
                      href={`mailto:${contact.email}`}
                      className="break-all hover:text-gold-soft transition-colors duration-2 ease-out"
                    >
                      {contact.email}
                    </a>
                  </li>
                )}
                {contact.phone && (
                  <li className="flex items-start gap-3">
                    <Phone
                      className="h-5 w-5 shrink-0 mt-0.5 text-gold-500"
                      aria-hidden="true"
                      strokeWidth={1.5}
                    />
                    <a
                      href={`tel:${contact.phone.replace(/\s+/g, '')}`}
                      className="hover:text-gold-soft transition-colors duration-2 ease-out"
                    >
                      {contact.phone}
                    </a>
                  </li>
                )}
                {contact.address && (
                  <li className="flex items-start gap-3">
                    <MapPin
                      className="h-5 w-5 shrink-0 mt-0.5 text-gold-500"
                      aria-hidden="true"
                      strokeWidth={1.5}
                    />
                    <span>{contact.address}</span>
                  </li>
                )}
                {contact.hours && (
                  <li className="flex items-start gap-3">
                    <Clock
                      className="h-5 w-5 shrink-0 mt-0.5 text-gold-500"
                      aria-hidden="true"
                      strokeWidth={1.5}
                    />
                    <span>{contact.hours}</span>
                  </li>
                )}
              </ul>
            </div>

            {/* ============ Columna 5 — Boletín ============ */}
            <div className="lg:border-l lg:border-gold-soft/20 lg:pl-8">
              <FooterHeading>Boletín</FooterHeading>
              <p className="text-body text-cream-100/80 leading-relaxed max-w-sm">
                Recibe inspiración, novedades y contenido para tu alma.
              </p>

              <form
                onSubmit={handleNewsletter}
                aria-label="Suscripción al boletín"
                className="mt-5"
                noValidate
              >
                <FormField label="Correo electrónico" hideLabel htmlFor="footer-newsletter-email">
                  <Input
                    id="footer-newsletter-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="Tu correo electrónico"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    // Override visual: campo crema legible sobre fondo violeta.
                    className={cn(
                      'bg-cream-50 text-primary-900 placeholder:text-primary-900/40',
                      'border-gold-500/50 focus:border-gold-500',
                    )}
                  />
                </FormField>

                <div className="mt-3">
                  <Button
                    type="submit"
                    variant="primary"
                    size="md"
                    loading={submitting}
                    fullWidth
                    leftIcon={<Sparkles aria-hidden="true" strokeWidth={1.5} />}
                    className={cn(
                      'bg-gold-500 text-primary-900 hover:bg-gold-300',
                      'border border-gold-500',
                    )}
                  >
                    Suscribirme
                  </Button>
                </div>
              </form>
            </div>
          </div>

          {/* ---- Bottom bar dentro de la tarjeta ---- */}
          <div
            className={cn(
              'relative border-t border-gold-soft/20',
              'px-8 py-5 md:px-10 lg:px-12',
              'flex flex-col gap-3 text-caption',
              'lg:flex-row lg:items-center lg:justify-between',
            )}
          >
            <p className="text-cream-100/65">
              © {year} Ceciluz Arte. Todos los derechos reservados.
            </p>
            <p className="hidden lg:inline-flex items-center gap-2 uppercase tracking-[0.16em] text-cream-100/70">
              <StarDot className="text-[10px]" />
              Arte que sana · Energía que transforma · Alma que conecta
              <StarDot className="text-[10px]" />
            </p>
            <ul className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <li>
                <Link
                  to={Routes.terms}
                  className="text-cream-100/70 hover:text-gold-soft transition-colors duration-2 ease-out"
                >
                  Términos
                </Link>
              </li>
              <li>
                <Link
                  to={Routes.privacy}
                  className="text-cream-100/70 hover:text-gold-soft transition-colors duration-2 ease-out"
                >
                  Privacidad
                </Link>
              </li>
              <li>
                <Link
                  to={Routes.cookies}
                  className="text-cream-100/70 hover:text-gold-soft transition-colors duration-2 ease-out"
                >
                  Cookies
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
}
